import {
  Box,
  CircularProgress,
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Label } from '@noco/http-client/lib/noco'
import React, { useCallback, useState } from 'react'
import { Button, Colors, DEFAULT_LABEL_COLOR, DynamicMuiIcon } from 'src/components/atoms'
import { LayoutTeam } from 'src/components/commons'
import { ModalLabelEdit } from 'src/components/modals/ModalLabelEdit'
import { ModalLabelDelete } from 'src/components/modals/ModalLabelDelete'
import { Header } from 'src/components/organisms'
import { useListLabels, useDeleteLabel } from 'src/fixtures/modules/labels/hooks'
import { useFeatureFlag } from 'src/fixtures/modules/user/hooks'

const useStyles = makeStyles(() =>
  createStyles({
    tableContainer: {
      backgroundColor: Colors.functional.background.default,
      borderRadius: '4px',
      border: `1px solid ${Colors.background.silver}`,
      boxShadow: 'none'
    },
    tableHead: {
      backgroundColor: Colors.functional.background.default
    },
    tableHeadCell: {
      fontWeight: 600,
      fontSize: '12px',
      color: Colors.base.middleGray,
      textTransform: 'uppercase',
      padding: '12px 16px',
      borderBottom: `1px solid ${Colors.background.silver}`
    },
    tableCell: {
      padding: '12px 16px',
      fontSize: '14px',
      color: Colors.base.black,
      borderBottom: `1px solid ${Colors.background.silver}`
    },
    labelChip: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '14px'
    },
    colorBox: {
      width: '32px',
      height: '32px',
      borderRadius: '4px',
      border: `1px solid ${Colors.background.silver}`
    },
    actionButton: {
      padding: '6px 12px',
      fontSize: '12px',
      borderRadius: '4px',
      border: `1px solid ${Colors.background.silver}`,
      backgroundColor: Colors.functional.background.default,
      color: Colors.base.black,
      cursor: 'pointer',
      marginRight: '8px',
      '&:hover': {
        backgroundColor: Colors.background.lightGray,
        borderColor: Colors.accent.keyPurple.default
      },
      '&:disabled': {
        opacity: 0.4,
        cursor: 'not-allowed'
      }
    },
    deleteButton: {
      '&:hover:not(:disabled)': {
        backgroundColor: '#FFF5F5',
        borderColor: Colors.accent.keyRed.default,
        color: Colors.accent.keyRed.default
      }
    }
  })
)

export const PageLabelManagement = () => {
  const classes = useStyles()
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [targetLabel, setTargetLabel] = useState<Label | undefined>()

  const { data: labelsData, isValidating } = useListLabels()
  const { handleDeleteLabel } = useDeleteLabel()
  const { hasPermission: isCreatable } = useFeatureFlag('label', 'create')
  const { hasPermission: isUpdatable } = useFeatureFlag('label', 'update')
  const { hasPermission: isDeletable } = useFeatureFlag('label', 'delete')

  const labels = labelsData?.labels ?? []

  const handleOpenCreateModal = useCallback(() => {
    setTargetLabel(undefined)
    setEditModalOpen(true)
  }, [])

  const handleOpenEditModal = useCallback((label: Label) => {
    setTargetLabel(label)
    setEditModalOpen(true)
  }, [])

  const handleOpenDeleteModal = useCallback((label: Label) => {
    setTargetLabel(label)
    setDeleteModalOpen(true)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setTargetLabel(undefined)
    setEditModalOpen(false)
  }, [])

  const handleCloseDeleteModal = useCallback(() => {
    setTargetLabel(undefined)
    setDeleteModalOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (targetLabel?.id) {
      const success = await handleDeleteLabel(targetLabel.id)
      if (success) {
        handleCloseDeleteModal()
      }
    }
  }, [targetLabel, handleDeleteLabel, handleCloseDeleteModal])

  const isLoading = isValidating && !labelsData

  return (
    <>
      <LayoutTeam>
        <Box
          height="100%"
          width={1}
          px="16px"
          pb="32px"
          bgcolor={Colors.functional.background.default}
          display="flex"
          flexDirection="column"
        >
          <Header
            textSize="large"
            iconSize="32px"
            icon="sellOutlined"
            title="ラベル管理"
            rightSection={
              <Button
                startIcon={<DynamicMuiIcon variant="add" size="16px" />}
                title="ラベルを追加"
                disabled={!isCreatable}
                onClick={handleOpenCreateModal}
              />
            }
            back={false}
          />
          <Box mt="16px" />
          <Box px="16px">
            <Typography variant="subtitle1" style={{ color: Colors.base.middleGray }}>
              資料に設定できるラベルを管理します。ラベルを追加・編集・削除することで、組織に合った分類ができます。
            </Typography>
          </Box>
          <Box mt="16px" mx="16px">
            {isLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center" height="200px">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer className={classes.tableContainer}>
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>ラベル名</TableCell>
                      <TableCell className={classes.tableHeadCell}>カラー</TableCell>
                      <TableCell className={classes.tableHeadCell}>使用中の資料数</TableCell>
                      <TableCell className={classes.tableHeadCell}>操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {labels.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" className={classes.tableCell}>
                          <Box py="24px">
                            <Typography variant="body2" style={{ color: Colors.base.middleGray }}>
                              ラベルがありません。「ラベルを追加」ボタンから新しいラベルを作成してください。
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      labels.map(label => (
                        <TableRow key={label.id} hover>
                          <TableCell className={classes.tableCell}>
                            <span
                              className={classes.labelChip}
                              style={{ backgroundColor: label.color || DEFAULT_LABEL_COLOR }}
                            >
                              {label.name}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <div
                              className={classes.colorBox}
                              style={{ backgroundColor: label.color || DEFAULT_LABEL_COLOR }}
                            />
                          </TableCell>
                          <TableCell className={classes.tableCell}>{label.documentsCount ?? 0}件</TableCell>
                          <TableCell className={classes.tableCell}>
                            <button
                              className={classes.actionButton}
                              onClick={() => handleOpenEditModal(label)}
                              disabled={!isUpdatable}
                            >
                              編集
                            </button>
                            <button
                              className={`${classes.actionButton} ${classes.deleteButton}`}
                              onClick={() => handleOpenDeleteModal(label)}
                              disabled={!isDeletable || label.isSystem || (label.documentsCount ?? 0) > 0}
                              title={
                                label.isSystem
                                  ? 'システムラベルは削除できません'
                                  : (label.documentsCount ?? 0) > 0
                                  ? '使用中のため削除できません'
                                  : ''
                              }
                            >
                              削除
                            </button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </LayoutTeam>
      <ModalLabelEdit open={editModalOpen} label={targetLabel} onClose={handleCloseEditModal} />
      <ModalLabelDelete
        open={deleteModalOpen}
        label={targetLabel}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
